import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { StatusChip } from '../components/data';
import { ErrorBanner, Loading, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import { EosHeadlines } from './eos/EosHeadlines';
import { EosIssues } from './eos/EosIssues';
import { EosRocks } from './eos/EosRocks';
import { EosScorecard } from './eos/EosScorecard';
import { EosTodos } from './eos/EosTodos';
import type { EosMeetingDetail } from './eos/eosTypes';

/**
 * The Level 10 agenda.
 *
 * Ninety minutes, seven sections, in this order and for these lengths. The
 * order is the method, not a preference: the numbers are reported before
 * anything is discussed, off-track measurables and rocks drop DOWN to the
 * issues list rather than being argued about in a five-minute section, and IDS
 * gets the hour that is left.
 *
 * The minutes are the standard L10 budget. They are shown as a target and not
 * enforced — a timer that cut somebody off mid-sentence would get the tab
 * closed on the first Monday.
 */
const SECTIONS = [
  {
    key: 'segue',
    name: 'Segue',
    minutes: 5,
    hint: 'Good news — one personal, one professional, from everybody.',
  },
  {
    key: 'scorecard',
    name: 'Scorecard',
    minutes: 5,
    hint: 'Report the numbers only. Anything off goal drops to the issues list.',
  },
  {
    key: 'rocks',
    name: 'Rock Review',
    minutes: 5,
    hint: 'On track or off track. No discussion — off track drops to the issues list.',
  },
  {
    key: 'headlines',
    name: 'Headlines',
    minutes: 5,
    hint: 'One sentence each of customer and employee news.',
  },
  {
    key: 'todos',
    name: 'To-Do List',
    minutes: 5,
    hint: 'Done or not done. The bar is 90%.',
  },
  {
    key: 'ids',
    name: 'IDS',
    minutes: 60,
    hint: 'Identify, Discuss, Solve — highest priority first. Solving usually creates a to-do.',
  },
  {
    key: 'conclude',
    name: 'Conclude',
    minutes: 5,
    hint: 'Recap the to-dos, agree the cascading messages, and rate the meeting 1–10.',
  },
] as const;

const TOTAL_MINUTES = SECTIONS.reduce((sum, s) => sum + s.minutes, 0);

function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * The meeting runner, on its own route rather than a tab.
 *
 * A running L10 is a mode, not a page you browse: the sidebar's other links
 * are all things to do instead of the meeting, and the section you are on has
 * to survive a reload. `CurrentSection` is persisted on every move for that
 * reason — a dropped laptop at minute fifty should not restart from Segue.
 */
export function EosMeetingRunPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const id = Number(meetingId);

  const [meeting, setMeeting] = useState<EosMeetingDetail | null>(null);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(() => Date.now());

  /* When the current section was entered, so its own clock can run
     independently of the meeting's. A ref rather than state: it is read by the
     render but changing it must not itself cause one — the interval below
     already re-renders every second. */
  const sectionEnteredAt = useRef(Date.now());

  /* When this runner was opened, which is the fallback the meeting clock falls
     back on for a meeting left running on an earlier day — see below. */
  const openedAt = useRef(Date.now());

  /* Starting is a POST, not a GET, because it stamps StartedUtc — so this
     cannot use useResource. Idempotent server-side: a reload of an already
     running meeting returns it unchanged rather than restarting the clock. */
  useEffect(() => {
    if (!Number.isInteger(id) || id <= 0) {
      setError('That is not a meeting id.');
      return;
    }
    api
      .post<EosMeetingDetail>(`/eos/meetings/${id}/start`)
      .then((detail) => {
        setMeeting(detail);
        setSectionIndex(Math.min(detail.CurrentSection ?? 0, SECTIONS.length - 1));
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Could not open that meeting.'),
      );
  }, [id]);

  useEffect(() => {
    const timer = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (error) {
    return (
      <>
        <ErrorBanner message={error} />
        <Link className="btn btn-ghost mt-4" to="/eos">
          Back to EOS
        </Link>
      </>
    );
  }
  if (!meeting) return <Loading label="Opening the meeting" />;

  if (meeting.Status === 'completed') {
    return <Recap meeting={meeting} />;
  }

  const section = SECTIONS[sectionIndex]!;
  /**
   * The meeting clock runs from `StartedUtc` — which is what makes it survive a
   * reload mid-meeting — but only while that timestamp belongs to TODAY.
   *
   * Two of the meetings imported from the standalone app were left in progress
   * on 16 July and never concluded. Measured from their real start, the clock
   * read `50887:49`, which is true and useless. A meeting reopened on a later
   * day is a new sitting, so the clock starts from when the runner opened; the
   * stored `StartedUtc` is left alone, because it is the record of when the
   * team first sat down and is not the timer's business to rewrite.
   */
  const started = meeting.StartedUtc ? new Date(meeting.StartedUtc) : null;
  const startedToday =
    started !== null && started.toDateString() === new Date(tick).toDateString();
  const startedAt = startedToday && started ? started.getTime() : openedAt.current;
  const elapsedTotal = (tick - startedAt) / 1000;
  const elapsedSection = (tick - sectionEnteredAt.current) / 1000;
  /* The budget UP TO AND INCLUDING this section, which is what says whether the
     meeting as a whole is behind — comparing against the 90-minute total would
     read as "on time" until minute eighty-nine. */
  const budgetSoFar = SECTIONS.slice(0, sectionIndex + 1).reduce((sum, s) => sum + s.minutes, 0) * 60;

  const goTo = (index: number) => {
    const next = Math.max(0, Math.min(SECTIONS.length - 1, index));
    setSectionIndex(next);
    sectionEnteredAt.current = Date.now();
    /* Fire and forget: losing the persisted section costs a reload starting one
       step back, and blocking the agenda on a write would be worse. */
    void api.put(`/eos/meetings/${id}`, { currentSection: next }).catch(() => undefined);
  };

  return (
    <>
      <PageHead
        title={meeting.Title}
        lede={`${meeting.TeamName} · ${formatDate(meeting.ScheduledDate)}${
          meeting.ScheduledTime ? ` at ${meeting.ScheduledTime.slice(0, 5)}` : ''
        }`}
        actions={
          <div className="eos-timer-bar">
            <span className={`eos-timer${elapsedTotal > budgetSoFar ? ' over' : ''}`}>
              {clock(elapsedTotal)}
            </span>
            <span className={`eos-pill ${elapsedTotal > budgetSoFar ? 'warning' : 'good'}`}>
              {elapsedTotal > budgetSoFar ? 'behind' : 'on time'}
            </span>
            <span className="xs subtle">{TOTAL_MINUTES} min planned</span>
            <Link className="btn btn-ghost btn-sm" to="/eos">
              Leave
            </Link>
          </div>
        }
      />

      {/* The agenda is a rail down the LEFT, which is where the app this came
          from put it. Laid out horizontally the seven sections read as tabs you
          might browse in any order, and an L10 agenda is the opposite of that:
          a fixed vertical list you work down once. */}
      <div className="eos-meeting-layout">
        <ol className="eos-agenda-rail">
          {SECTIONS.map((s, index) => (
            <li key={s.key}>
              <button
                type="button"
                className={`eos-agenda-item${index === sectionIndex ? ' current' : ''}${
                  index < sectionIndex ? ' past' : ''
                }`}
                onClick={() => goTo(index)}
              >
                <span className="num">{index + 1}</span>
                <span className="name">{s.name}</span>
                <span className="mins">{s.minutes} min</span>
              </button>
            </li>
          ))}
        </ol>

        <div className="eos-meeting-body">
          <section className="card">
            <div className="card-head row-between wrap">
              <div>
                <h3>{section.name}</h3>
                <p className="xs subtle">{section.hint}</p>
              </div>
              <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
                <span
                  className={`eos-timer${elapsedSection > section.minutes * 60 ? ' over' : ''}`}
                >
                  {clock(elapsedSection)}
                </span>
                <span className="xs subtle">of {section.minutes} min</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={sectionIndex === 0}
                  onClick={() => goTo(sectionIndex - 1)}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={sectionIndex === SECTIONS.length - 1}
                  onClick={() => goTo(sectionIndex + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </section>

      {/* Each section reuses the tab that owns it rather than a meeting-only
          copy: a to-do added in the meeting has to be the same to-do the To-Dos
          tab shows on Tuesday, and two components writing the same table would
          drift. `meetingId` is what makes what happens here attributable to
          this meeting. */}
          {section.key === 'segue' && (
            <div className="card">
              <div className="card-body">
                <div className="eos-segue">
                  <strong>Nothing to record.</strong>
                  Round the room — one personal and one professional piece of good news each — then
                  move on.
                </div>
              </div>
            </div>
          )}
          {section.key === 'scorecard' && (
            /* Reordering is off inside the meeting: Scorecard is five minutes of
               reading the numbers out, not of rearranging them. */
            <EosScorecard
              teamId={meeting.EosTeamId}
              reorderable={false}
              onChanged={() => undefined}
            />
          )}
          {section.key === 'rocks' && (
            <EosRocks teamId={meeting.EosTeamId} onChanged={() => undefined} />
          )}
          {section.key === 'headlines' && (
            <EosHeadlines teamId={meeting.EosTeamId} onChanged={() => undefined} />
          )}
          {section.key === 'todos' && (
            <EosTodos teamId={meeting.EosTeamId} meetingId={id} onChanged={() => undefined} />
          )}
          {section.key === 'ids' && (
            <EosIssues teamId={meeting.EosTeamId} meetingId={id} onChanged={() => undefined} />
          )}
          {section.key === 'conclude' && (
            <Conclude
              meeting={meeting}
              onConcluded={(detail) => setMeeting(detail)}
              onFailed={setError}
              onLeave={() => navigate('/eos')}
            />
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Conclude — the ratings and the cascading messages.
 *
 * Ratings default to unset rather than to 8. That app pre-filled an 8, which
 * makes a room that says nothing look like a room that scored the meeting — and
 * the average is the one number the cadence is judged on, so it must only ever
 * count people who actually answered.
 */
function Conclude({
  meeting,
  onConcluded,
  onFailed,
  onLeave,
}: {
  meeting: EosMeetingDetail;
  onConcluded: (detail: EosMeetingDetail) => void;
  onFailed: (message: string) => void;
  onLeave: () => void;
}) {
  const [ratings, setRatings] = useState<Record<number, number | null>>(() =>
    Object.fromEntries(meeting.attendees.map((a) => [a.EmployeeId, a.Rating])),
  );
  const [notes, setNotes] = useState(meeting.Notes ?? '');
  const [saving, setSaving] = useState(false);

  const given = Object.values(ratings).filter((r) => r !== null) as number[];
  const average = given.length ? given.reduce((a, b) => a + b, 0) / given.length : null;

  const conclude = async () => {
    setSaving(true);
    try {
      const detail = await api.post<EosMeetingDetail>(`/eos/meetings/${meeting.MeetingId}/conclude`, {
        ratings: Object.entries(ratings).map(([employeeId, rating]) => ({
          employeeId: Number(employeeId),
          rating,
        })),
        notes: notes.trim() || null,
      });
      onConcluded(detail);
    } catch (err) {
      onFailed(err instanceof Error ? err.message : 'Could not conclude the meeting.');
      setSaving(false);
    }
  };

  return (
    <>
      <section className="card">
        <div className="card-head row-between wrap">
          <div>
            <h3>Rate the meeting</h3>
            <p className="xs subtle">
              1–10 from everybody. An L10 is supposed to be an 8 or better — that is where the name
              comes from.
            </p>
          </div>
          {average !== null && (
            <span
              className={`eos-pill ${average >= 8 ? 'good' : average >= 6 ? 'warning' : 'serious'}`}
            >
              {average.toFixed(1)} average
            </span>
          )}
        </div>
        <div className="card-body">
          {meeting.attendees.length === 0 && (
            <p className="muted">Nobody was invited to this meeting, so there is nothing to rate.</p>
          )}
          {meeting.attendees.map((attendee) => (
            <div key={attendee.EmployeeId} className="eos-rating-row">
              <span className="grow">{attendee.FullName}</span>
              <div className="scale" role="group" aria-label={`Rating from ${attendee.FullName}`}>
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={ratings[attendee.EmployeeId] === n ? 'on' : ''}
                    aria-pressed={ratings[attendee.EmployeeId] === n}
                    /* Clicking the chosen number clears it, the same rule
                       ScaleInput uses — otherwise a mis-click can never be
                       taken back to "did not rate". */
                    onClick={() =>
                      setRatings((current) => ({
                        ...current,
                        [attendee.EmployeeId]: current[attendee.EmployeeId] === n ? null : n,
                      }))
                    }
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <h3>Cascading messages</h3>
          <p className="xs subtle">What leaves this room, and who has to be told.</p>
        </div>
        <div className="card-body">
          <textarea
            className="textarea"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="row mt-4" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving}
              onClick={() => void conclude()}
            >
              {saving ? 'Closing…' : 'Conclude meeting'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={onLeave}>
              Leave without concluding
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * The recap of a concluded meeting.
 *
 * What it produced, not what was said: the to-dos it created, the issues it
 * solved, the rating and the cascading messages. This is also what opening a
 * past meeting from the history table shows.
 */
function Recap({ meeting }: { meeting: EosMeetingDetail }) {
  return (
    <>
      <PageHead
        title={meeting.Title}
        lede={`${meeting.TeamName} · ${formatDate(meeting.ScheduledDate)} · concluded`}
        actions={
          <Link className="btn btn-ghost btn-sm" to="/eos">
            Back to EOS
          </Link>
        }
      />

      <div className="grid grid-3">
        <div className="stat">
          <div className="label">Rating</div>
          <div className="figure">
            {meeting.AverageRating === null ? '—' : Number(meeting.AverageRating).toFixed(1)}
          </div>
          <div className="foot">
            {meeting.attendees.filter((a) => a.Rating !== null).length} of {meeting.attendees.length}{' '}
            rated it
          </div>
        </div>
        <div className="stat">
          <div className="label">Issues solved</div>
          <div className="figure">{meeting.solvedIssues.length}</div>
          <div className="foot">worked through IDS</div>
        </div>
        <div className="stat">
          <div className="label">To-dos raised</div>
          <div className="figure">{meeting.todos.length}</div>
          <div className="foot">
            {meeting.todos.filter((t) => t.IsDone).length} already done
          </div>
        </div>
      </div>

      <section className="card mt-4">
        <div className="card-head">
          <h3>What came out of it</h3>
        </div>
        <div className="card-body">
          <div className="xs subtle">Issues solved</div>
          {meeting.solvedIssues.length === 0 && <p className="muted mt-2">None recorded.</p>}
          {meeting.solvedIssues.map((issue) => (
            <div key={issue.IssueId} className="mt-2">
              <span>{issue.Title}</span>
              {issue.Resolution && <div className="xs subtle">{issue.Resolution}</div>}
            </div>
          ))}

          <div className="divider mt-4" />
          <div className="xs subtle">To-dos</div>
          {meeting.todos.length === 0 && <p className="muted mt-2">None recorded.</p>}
          {meeting.todos.map((todo) => (
            <div key={todo.TodoId} className="row mt-2" style={{ gap: 8 }}>
              <StatusChip band={todo.IsDone ? 'good' : 'neutral'}>
                {todo.IsDone ? 'done' : 'open'}
              </StatusChip>
              <span className="grow">{todo.Title}</span>
              <span className="xs subtle">{todo.OwnerName ?? 'Unassigned'}</span>
            </div>
          ))}

          {meeting.Notes && (
            <>
              <div className="divider mt-4" />
              <div className="xs subtle">Cascading messages</div>
              <p className="small mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                {meeting.Notes}
              </p>
            </>
          )}
        </div>
      </section>

      <section className="card mt-4">
        <div className="card-head">
          <h3>Ratings</h3>
        </div>
        <div className="card-body">
          {meeting.attendees.map((attendee) => (
            <div key={attendee.EmployeeId} className="row" style={{ gap: 8 }}>
              <span className="grow">{attendee.FullName}</span>
              <span className="mono">{attendee.Rating ?? '—'}</span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
